import { ComparisonInterval } from '../types/ComparisonInterval'
import { VersionAggregates } from '../types/UpdateImpact'

/**
 * Encapsulates calculation logic for update impact aggregate values
 */
export class UpdateImpactAggregates {
  _aggregates: VersionAggregates
  _interval: ComparisonInterval
  _intervalMap: MappedImpactInternalValues

  constructor(aggregates: VersionAggregates = defaultAggregate, interval: ComparisonInterval = ComparisonInterval.DAYS_7) {
    this._aggregates = aggregates || {}
    this._interval = interval
    this._intervalMap = ImpactIntervalToAggregateValueMapper[interval]
  }

  get downloadsImpact() {
    return this._aggregates[this._intervalMap.downloadsImpact]
  }

  get downloadsTrend() {
    return computeTrend(this._aggregates[this._intervalMap.downloads], this._aggregates[this._intervalMap.downloadsBase])
  }

  get revenueImpact() {
    return this._aggregates[this._intervalMap.revenueImpact]
  }

  get revenueTrend() {
    return computeTrend(this._aggregates[this._intervalMap.revenue], this._aggregates[this._intervalMap.revenueBase])
  }

  get revenueAndDownloadsRatio(): number | undefined {
    const downloads = this._aggregates[this._intervalMap.downloads]
    const downloadsBase = this._aggregates[this._intervalMap.downloadsBase]
    if (!Number.isFinite(downloads) || !Number.isFinite(downloadsBase)) return undefined
    // Both periods have zero downloads: ratios are undefined for both, but
    // the change is symmetric (no data ↔ no data). Treat as 0 to match the
    // trend convention (`revenueAndDownloadsRatioTrend` returns 0 here) and
    // avoid surfacing a misleading "no data available" indicator for an
    // entirely empty version.
    if (downloads === 0 && downloadsBase === 0) return 0
    // Exactly one period has zero downloads: the ratio is undefined for that
    // period only, so the change between them is mathematically meaningless.
    // Return literal `undefined` so the UI can render the explicit N/A icon.
    if (downloads === 0 || downloadsBase === 0) return undefined
    const ratio = normalizeValues(this._aggregates[this._intervalMap.revenue] / downloads)
    const baseRatio = normalizeValues(this._aggregates[this._intervalMap.revenueBase] / downloadsBase)
    return ratio - baseRatio
  }

  get revenueAndDownloadsRatioTrend() {
    const downloads = this._aggregates[this._intervalMap.downloads]
    const downloadsBase = this._aggregates[this._intervalMap.downloadsBase]
    // A period with zero downloads has an undefined revenue/downloads ratio.
    // Treat it as 0 so computeTrend yields ±1 in asymmetric cases (one period
    // with data, the other without) and 0 when both periods are empty —
    // matching the convention used by revenueTrend / downloadsTrend.
    const ratio = downloads ? this._aggregates[this._intervalMap.revenue] / downloads : 0
    const baseRatio = downloadsBase ? this._aggregates[this._intervalMap.revenueBase] / downloadsBase : 0
    return computeTrend(ratio, baseRatio)
  }

  get revenueAndDownloadsRatioImpact(): number | undefined {
    const ratio = this.revenueAndDownloadsRatio
    if (ratio === undefined) return undefined
    const baseRatio = normalizeValues(getTrendValue(this._aggregates[this._intervalMap.revenueBase] / this._aggregates[this._intervalMap.downloadsBase]))
    return ratio - baseRatio
  }
}

const ImpactIntervalToAggregateValueMapper: { [interval in ComparisonInterval]: MappedImpactInternalValues } = {
  [ComparisonInterval.DAYS_7]: {
    downloadsImpact: 'week1DownloadsImpact',
    revenueImpact: 'week1RevenueImpact',
    downloadsBase: 'baseWeek1Downloads',
    revenueBase: 'baseWeek1Revenue',
    downloads: 'week1Downloads',
    revenue: 'week1Revenue',
  },
  [ComparisonInterval.DAYS_14]: {
    downloadsImpact: 'week2DownloadsImpact',
    revenueImpact: 'week2RevenueImpact',
    downloadsBase: 'baseWeek2Downloads',
    revenueBase: 'baseWeek2Revenue',
    downloads: 'week2Downloads',
    revenue: 'week2Revenue',
  },
  [ComparisonInterval.DAYS_30]: {
    downloadsImpact: 'month1DownloadsImpact',
    revenueImpact: 'month1RevenueImpact',
    downloadsBase: 'baseMonth1Downloads',
    revenueBase: 'baseMonth1Revenue',
    downloads: 'month1Downloads',
    revenue: 'month1Revenue',
  },
}

type MappedImpactInternalValues = {
  downloadsImpact: keyof VersionAggregates
  revenueImpact: keyof VersionAggregates
  downloadsBase: keyof VersionAggregates
  revenueBase: keyof VersionAggregates
  downloads: keyof VersionAggregates
  revenue: keyof VersionAggregates
}

const normalizeValues = (value: number) => {
  if (isNaN(value) || !isFinite(value)) {
    return 0
  } else {
    return value
  }
}

const getTrendValue = (value: number) => {
  return (1 - value) * -1
}

/**
 * Compute the percentage trend between `current` and `base` while handling
 * the edge cases where one of the periods has zero revenue / downloads.
 *
 * - Both periods zero (or both undefined / missing): no change.
 * - Base period zero, current period non-zero: division-by-zero would yield
 *   ±Infinity. Treat as +100% (or -100% if current is negative — defensive).
 * - Current period zero, base period non-zero: -100% trend.
 * - Otherwise: standard `(current / base) - 1`.
 */
const computeTrend = (current: number, base: number): number => {
  if (!base) {
    if (!current) return 0
    return current > 0 ? 1 : -1
  }
  return normalizeValues(getTrendValue(current / base))
}

const defaultAggregate = {
  baseWeek1Revenue: 0,
  baseWeek1Downloads: 0,
  baseWeek2Revenue: 0,
  baseWeek2Downloads: 0,
  baseMonth1Revenue: 0,
  baseMonth1Downloads: 0,
  week1Revenue: 0,
  week1Downloads: 0,
  week2Revenue: 0,
  week2Downloads: 0,
  month1Revenue: 0,
  month1Downloads: 0,
  week1RevenueImpact: 0,
  week1DownloadsImpact: 0,
  week2RevenueImpact: 0,
  week2DownloadsImpact: 0,
  month1RevenueImpact: 0,
  month1DownloadsImpact: 0,
}
